import { useCallback, useEffect, useMemo } from "react";
import { ConnectedProps, connect } from "react-redux";
import { AppDispatch, RootState } from "store";
import Invoice from "./Invoice";
import {
  actions as preliActions,
  preliquidationSelector,
} from "reducers/preliquidation";
import { actions as notificationActions } from "reducers/notification";
import {
  InvoiceFormType,
  detailPreliquidationInvoiceContainerPropsType,
} from "./types";
import { useHistory, useParams } from "react-router-dom";
import { UploadInvoiceFileMiddlewareType } from "sagas/types/preliquidation";
import moment from "moment";
import {
  BULK_FLOW_ID,
  DATE_FORMATS,
  MAX_FILE_SIZE,
  TAX_PAYER_TYPES,
  PRELIQUIDATION_STATUS,
  FILE_EXT_INVOICE,
} from "utils/constants";
import { getBase64FromUrl, isBase64, toBase64 } from "utils/files";
import {
  InvoiceFileStatus,
  DetailInvoiceType,
  DetailPreliquidationType,
} from "reducers/types/preliquidation";
import { NotificationStateType } from "reducers/types/notification";
import { sessionSelector, actions as sessionActions } from "reducers/session";
import { validationSchemaAR } from "./invoiceData/AR/schemaAR";
import { validationSchemaCO } from "./invoiceData/CO/schemaCO";
import { DEFAULT_COUNTRY } from "hooks/useSession";
import { useTranslation } from "react-i18next";
import "./i18n";
import { validationSchemaMX } from "./invoiceData/MX/schemaMX";
import useBasicData from "hooks/useBasicData";
import { validationSchemaCL } from "./invoiceData/CL/schemaCL";
import { PagesPreliquidationTypes } from "../types";

const InvoiceContainer = (
  props: detailPreliquidationInvoiceContainerPropsType & ConnectorProps
): JSX.Element => {
  const params: { id?: string } = useParams();
  const { t } = useTranslation();
  const history = useHistory();
  const { invoiceTypes } = useBasicData(["invoiceTypes"]);

  const minDate = useMemo(() => {
    const date = props.detailPreliquidations?.generatedAt;
    const generatedAt = moment(date || undefined);
    return generatedAt.subtract(1, "day");
  }, [props.detailPreliquidations?.generatedAt]);

  useEffect(() => {
    props.setInvoiceFileStatus({ loading: false, error: false });

    if (params.id === BULK_FLOW_ID) {
      const { invoiceIds, currentInvoice } = props;
      const len = invoiceIds?.length;
      if (len > 0 && currentInvoice < len) {
        props.getInvoiceDetail(invoiceIds[currentInvoice].toString());
      } else {
        history.replace(`/${props.countryCode}/presettlements`);
      }
    } else {
      const id = Number(params.id);
      if (isNaN(id)) {
        history.replace(`/${props.countryCode}/presettlements`);
      } else {
        props.getInvoiceDetail(id.toString());
      }
    }

    return () => {
      props.resetInvoiceDetail();
      props.resetInvoiceFileStatus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const presettlementId = useMemo(() => {
    const len = props.invoiceIds?.length;
    if (params.id === BULK_FLOW_ID && len > 0 && props.currentInvoice < len) {
      return props.invoiceIds[props.currentInvoice].toString();
    }

    return params?.id || "";
  }, [params.id, props.invoiceIds, props.currentInvoice]);

  const fileHandler = async (file: File) => {
    props.setInvoiceFileStatus({ loading: true });
    const isCLCountry = props.countryCode === "CL";

    if (file.size > MAX_FILE_SIZE || !FILE_EXT_INVOICE.includes(file.type)) {
      props.setInvoiceFileStatus({
        error: true,
        loading: false,
        message: "invoice:invalidFile",
      });
      return;
    } try {
        const base64 = (await toBase64(file)) as string;
        const uploaded = props.invoiceDetail?.invoiceFile?.upload;
        const body = {
          id: params.id ? parseInt(params.id) : 0,
          content: base64,
        }

        const uploadInvoice = () => ( isCLCountry ? props.uploadInvoiceFile(
          body
        ) : props.uploadInvoiceFileText(
          body
        ));

        const replaceInvoice = () => ( isCLCountry ? props.replaceInvoiceFile(
          body
        ): props.replaceInvoiceFileText(
          body
        ));
        
        !uploaded
          ? uploadInvoice()
          : replaceInvoice()

      } catch (err) {
      console.log("Base64 error: ", err);
    }
  };

  const deleteFile = () => {
    props.showNotification({
      id: "notification-delete",
      level: "warning",
      title: t("invoice:deleteInvoiceTitle"),
      body: t("invoice:deleteInvoiceMessage"),
      onConfirmLabel: "invoice:delete",
      onCancelLabel: "invoice:cancel",
      onConfirm: () => props.deleteInvoiceFile(parseInt(params.id || "0")),
    });
  };

  const goToPreviousFile = () => {
    props.setInvoiceFileStatus({
      error: false,
      loading: false,
    });
  };

  const downloadFile = async () => {
    const url = props.invoiceDetail?.invoiceFile?.url;
    if (!url) return;

    const now = new Date();
    const date = `${now.getDate()}_${now.getMonth() + 1}_${now.getFullYear()}`;
    const downloadLink = document.createElement("a");
    const fileName = `preli-${params.id || ""}-${date}.pdf`;

    if (isBase64(url)) {
      downloadLink.href = url;
    } else {
      const base64 = (await getBase64FromUrl(url)) as string;
      downloadLink.href = base64;
    }
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const formatInitialValues = (
    detailInvoice: DetailInvoiceType,
    detailPreliquidation: DetailPreliquidationType
  ) => {
    const { emisionDate } = detailInvoice;
    return {
      ...detailInvoice,
      caeNumber: detailInvoice.caeNumber || undefined,
      invoiceNumber: detailInvoice.invoiceNumber || undefined,
      region: detailPreliquidation.region?.name || "-",
      salePoint: detailInvoice.salePoint || undefined,
      invoiceType: detailInvoice.invoiceType || invoiceTypes[0],
      emisionDate: detailInvoice.emisionDate
        ? moment(emisionDate).format(DATE_FORMATS.extraShortDate)
        : undefined,
      fiscalData: {
        ...detailInvoice.fiscalData,
        taxPayerType: t(TAX_PAYER_TYPES[detailInvoice.fiscalData.taxPayerType]),
      },
    };
  };

  const initialValues = useMemo(() => {
    return formatInitialValues(
      props.invoiceDetail,
      props.detailPreliquidations
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.invoiceDetail.id,
    props.detailPreliquidations.status,
    invoiceTypes,
    props.isFetching,
  ]);

  const isBulkFlow = useMemo(() => {
    return params.id === BULK_FLOW_ID && props?.invoiceIds?.length > 0;
  }, [props?.invoiceIds?.length, params.id]);

  const isFormEnabled = useMemo(() => {
    const status = props.detailPreliquidations.status;
    return (
      (isBulkFlow && status === PRELIQUIDATION_STATUS.IN_APPROVEMENT) ||
      (!isBulkFlow &&
        [
          PRELIQUIDATION_STATUS.PENDING,
          PRELIQUIDATION_STATUS.IN_APPROVEMENT,
        ].includes(status) &&
        props.invoiceDetail?.invoiceFile?.upload)
    );
  }, [
    props.detailPreliquidations.status,
    props.invoiceDetail.invoiceFile.upload,
    isBulkFlow,
  ]);

  const isFormEnabledCL = useMemo(() => {
    const status = props.detailPreliquidations.status;
    return (
      (isBulkFlow && status === PRELIQUIDATION_STATUS.IN_APPROVEMENT) ||
      (!isBulkFlow &&
        [
          PRELIQUIDATION_STATUS.PENDING,
          PRELIQUIDATION_STATUS.IN_APPROVEMENT,
        ].includes(status) &&
        props.invoiceDetail?.invoiceFile?.upload)
    );
  }, [
    isBulkFlow,
    props.detailPreliquidations.status,
    props.invoiceDetail.invoiceFile.upload,
  ]);

  const disableDeleteReplaceIcon = useMemo(() => {
    if (isBulkFlow) return true;
    const { status } = props.detailPreliquidations;
    return !isFormEnabled && status !== PRELIQUIDATION_STATUS.INVOICE_REJECTED;
  }, [isBulkFlow, isFormEnabled, props.detailPreliquidations]);

  const disableRejectInvoice = useCallback(
    (invalid: boolean) => {
      const { status } = props.detailPreliquidations;
      const notUploaded = !props.invoiceDetail.invoiceFile?.upload;
      const fileError = props.invoiceFileStatus.error;
      const invalidState = ![
        PRELIQUIDATION_STATUS.IN_APPROVEMENT,
        PRELIQUIDATION_STATUS.APPROVED,
        PRELIQUIDATION_STATUS.REGULATORY_VALIDATION_FAILED,
      ].includes(status);

      const disableOnBulkFlow = isBulkFlow && invalidState;
      const disableOnNormalFlow =
        !isBulkFlow &&
        (props.dirty || notUploaded || fileError || invalidState || invalid);
      return disableOnBulkFlow || disableOnNormalFlow;
    },
    [
      isBulkFlow,
      props.detailPreliquidations,
      props.dirty,
      props.invoiceDetail.invoiceFile?.upload,
      props.invoiceFileStatus.error,
    ]
  );

  const validationSchema = useCallback(
    (save: boolean) => {
      if (props.countryCode === "CO") return validationSchemaCO(save);
      if (props.countryCode === "MX") return validationSchemaMX(save);
      if (props.countryCode === "CL") return validationSchemaCL(save);
      return validationSchemaAR(save);
    },
    [props.countryCode]
  );

  return (
    <Invoice
      {...props}
      invoiceTypes={invoiceTypes}
      isBulkFlow={isBulkFlow}
      disableRejectInvoice={disableRejectInvoice}
      disableDeleteReplaceIcon={disableDeleteReplaceIcon}
      minDate={minDate}
      presettlementId={presettlementId}
      fileHandler={fileHandler}
      deleteFile={deleteFile}
      downloadFile={downloadFile}
      validationSchema={validationSchema}
      initialValues={initialValues}
      isFormEnabled={isFormEnabled}
      isFormEnabledCL={isFormEnabledCL}
      goToPreviousFile={goToPreviousFile}
      testId="invoice"
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  invoiceDetail: preliquidationSelector(state).invoiceDetail,
  detailPreliquidations: preliquidationSelector(state).detailPreliquidations,
  invoiceFileStatus: preliquidationSelector(state).invoiceFileStatus,
  approvingInvoice: preliquidationSelector(state).approvingInvoice,
  keepDirty: preliquidationSelector(state).keepDirty,
  dirty: sessionSelector(state).dirtyForm,
  countryCode: sessionSelector(state).countryCode || DEFAULT_COUNTRY,
  invoiceIds: preliquidationSelector(state).pendingApprovalPresettlements,
  currentInvoice: preliquidationSelector(state).currentInvoice,
  isFetching: sessionSelector(state).fetching,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getInvoiceDetail: (params: string | undefined) => {
    dispatch(preliActions.getInvoiceDetailRequest(params));
  },
  getInvoiceDetailSave: (params: InvoiceFormType) => {
    dispatch(preliActions.getInvoiceDetailSaveRequest(params));
  },
  getInvoiceDetailApprove: (params: InvoiceFormType) => {
    dispatch(preliActions.getInvoiceDetailApproveFetch(params));
  },
  uploadInvoiceFileText: (params: UploadInvoiceFileMiddlewareType) => {
    dispatch(preliActions.uploadInvoiceFileTextFetch(params));
  },
  uploadInvoiceFile: (params: UploadInvoiceFileMiddlewareType) => {
    dispatch(preliActions.uploadInvoiceFileFetch(params));
  },
  replaceInvoiceFileText: (params: UploadInvoiceFileMiddlewareType) => {
    dispatch(preliActions.replaceInvoiceFileTextFetch(params));
  },
  replaceInvoiceFile: (params: UploadInvoiceFileMiddlewareType) => {
    dispatch(preliActions.replaceInvoiceFileFetch(params));
  },
  deleteInvoiceFile: (id: number) => {
    dispatch(preliActions.deleteInvoiceFileRequest({ id }));
  },
  resetInvoiceDetail: () => {
    dispatch(preliActions.resetInvoiceDetail());
  },
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  setInvoiceFileStatus: (params: InvoiceFileStatus) => {
    dispatch(preliActions.setInvoiceFileStatus(params));
  },
  resetInvoiceFileStatus: () => {
    dispatch(preliActions.resetInvoiceFileStatus());
  },
  toggleRejectInvoiceModalVisibility: (visibility: boolean) => {
    dispatch(preliActions.toggleRejectInvoiceModalVisibility(visibility));
  },
  setActualPage: (page: PagesPreliquidationTypes) => {
    dispatch(preliActions.setActualPage(page));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(InvoiceContainer);

